import * as THREE from "../../../libs/three.js/build/three.module.js";
import {EventDispatcher} from "../../EventDispatcher.js";
import {ClipTask, ClipMethod} from "../../defines.js";
import {Utils} from "../../utils.js";
import {t} from "../../digitization/i18n/index.js";
import {SnitVolume, snitDefinitionFromBox} from "./SnitVolume.js";
import {BoxVolume} from "../../utils/Volume.js";
import {snitplanFacePlanes, orientationFromNormal, snitplanFromThreePoints, SnitDisplayMode, SnitOrientation} from "./snitplanGeometry.js";

// Snitplaner i en viewer (viewer.snitplan). Ejer livscyklussen for ALLE Snitplan-objekter og den fælles clip-tilstand - UI'et
// (PointView-popup, træ-række) er kun en visning af dette.
//
// Ét Snitplan = ét objekt (en SnitVolume i scene.volumes). Adaptere afleder renderer-specifik clipping fra samme definition:
//   - punktskyer: SnitVolume er en Potree-clip-boks (clip-puljen, composeClipBoxLocalMatrices, CPU-nodebeskæring ved SHOW_INSIDE)
//   - IFC/OBJ-modeller: getIfcClipPlanes() = de seks indadvendte planer afledt af definitionen (værten sætter dem som clippingPlanes)
//
// GLOBAL CLIP-TILSTAND. Potrees clipTask/clipMethod gælder hele viewer'en og deles med Clip-dialogen, profilerne m.fl. Derfor:
//   - Normal: Snitplanet er IKKE i clip-puljen (clip = false) og rører ikke clipTask/clipMethod.
//   - Fremhæv (HIGHLIGHT) / Vis indenfor (SHOW_INSIDE) / Vis udenfor (SHOW_OUTSIDE), INSIDE_ANY: højst ÉT Snit ad gangen; aktiveres et andet, går det
//     tidligere tilbage til Normal. Den globale tilstand gemmes ved første aktivering og genoprettes, når intet Snitplan længere er
//     aktivt (kun hvis ingen andre har ændret den undervejs - ellers overtager de, og Snitplanet slipper uden at røre den).
//     Mens et Snitplan er aktivt, følger øvrige clip-objekter (Clip-dialogen, profiler) den samme globale tilstand.

export class SnitplanManager extends EventDispatcher{

	constructor(viewer){
		super();
		this.viewer = viewer;
		this._saved = null;      // {task, method} før første aktivering
		this._applied = null;    // {task, method} vi selv har sat
		this._applying = false;
		this._listening = false;
		this.version = 0;        // tælles op ved enhver ændring (IFC-adapteren genberegner kun ved ny version)
		this._ifcCache = {version: -1, planes: []};
		this._ifcHighlightCache = {version: -1, planes: []};
		this._ifcOutsideCache = {version: -1, planes: []};
		// Det Snitplan, der lige nu er aktivt i Fremhæv/Skjul alt andet (ellers null). Mens et Snitplan er aktivt, er det den ENESTE
		// kilde til clipping/fremhævning: viewer.update og IFC-adapteren udelader øvrige Clip-volumener (activeVolume != null).
		this.activeVolume = null;

		this._onClipStateChanged = () => this._externalClipChange();
		this._onVolumeRemoved = (e) => this._volumeRemoved(e.volume);
		this._onSnitChanged = () => this._changed();
		this._onVisibility = () => this._apply();
		this._onSceneChanged = (e) => this._rebindScene(e);
	}

	// ---- opslag -------------------------------------------------------------------------------------------------------

	list(){
		const scene = this.viewer.scene;
		return scene ? scene.volumes.filter((v) => v.pvSnit) : [];
	}

	get(id){
		return this.list().find((v) => v.snit.id === id) || null;
	}

	serialize(){
		return this.list().map((v) => v.definition);
	}

	// ---- livscyklus ---------------------------------------------------------------------------------------------------

	// Idempotent på id: findes Snittet allerede (fx indlæsning af opsætning to gange), returneres det eksisterende.
	// activate: et NYT Snit starter i Fremhæv (det tidligere aktive går tilbage til Ingen); indlæste Snit starter i Ingen.
	add(definition = {}, {activate = false} = {}){
		const existing = definition.id ? this.get(definition.id) : null;
		if(existing) return existing;

		const volume = new SnitVolume(Object.assign({}, definition, {name: definition.name || this._nextName()}));
		this._track(volume);
		this.viewer.scene.addVolume(volume);
		this._changed();
		this.dispatchEvent({type: "snit_added", snit: volume});
		if(activate) this.setDisplayMode(volume.snit.id, SnitDisplayMode.HIGHLIGHT);
		return volume;
	}

	// Indlæsning af gemte definitioner (settings/setup). Alle starter i Normal.
	restore(definitions){
		(definitions || []).forEach((definition) => this.add(definition));
	}

	remove(id){
		const volume = typeof id === "string" ? this.get(id) : id;
		if(!volume) return false;
		this.viewer.scene.removeVolume(volume);
		return true;
	}

	_nextName(){
		let max = 0;
		for(const v of this.list()){
			const m = /(\d+)\s*$/.exec(v.snit.name || "");
			if(m) max = Math.max(max, parseInt(m[1], 10));
		}
		return t("Snit {0}", Math.max(max, this.list().length) + 1);
	}

	_track(volume){
		this._ensureListening();
		volume.addEventListener("snit_changed", this._onSnitChanged);
		volume.addEventListener("visibility_changed", this._onVisibility);
	}

	_untrack(volume){
		volume.removeEventListener("snit_changed", this._onSnitChanged);
		volume.removeEventListener("visibility_changed", this._onVisibility);
	}

	_ensureListening(){
		if(this._listening) return;
		this._listening = true;
		this.viewer.scene.addEventListener("volume_removed", this._onVolumeRemoved);
		this.viewer.addEventListener("scene_changed", this._onSceneChanged);
		this.viewer.addEventListener("cliptask_changed", this._onClipStateChanged);
		this.viewer.addEventListener("clipmethod_changed", this._onClipStateChanged);
	}

	_stopListening(){
		if(!this._listening) return;
		this._listening = false;
		this.viewer.scene.removeEventListener("volume_removed", this._onVolumeRemoved);
		this.viewer.removeEventListener("scene_changed", this._onSceneChanged);
		this.viewer.removeEventListener("cliptask_changed", this._onClipStateChanged);
		this.viewer.removeEventListener("clipmethod_changed", this._onClipStateChanged);
	}

	_rebindScene(e){
		if(e.oldScene) e.oldScene.removeEventListener("volume_removed", this._onVolumeRemoved);
		if(e.scene) e.scene.addEventListener("volume_removed", this._onVolumeRemoved);
	}

	_volumeRemoved(volume){
		if(!volume || !volume.pvSnit) return;
		this._untrack(volume);
		volume.clip = false;
		this._apply();
		this.dispatchEvent({type: "snit_removed", snit: volume});
		if(this.list().length === 0) this._stopListening();
	}

	// ---- placering ----------------------------------------------------------------------------------------------------

	// Oprettelsesflows (alle ender i ét og samme fælles Snitplan; ingen specialtyper):
	//   startPlacement({orientation})  lodret/vandret fra ét udpeget centerpunkt (Potrees egen volumen-placering)
	//   startThreePoints()             plan udspændt af tre udpegede punkter i punktskyen
	//   addPlaced(definition)          færdig definition (fx fra en IFC-flade) - samme afslutning som de øvrige
	// Afslutning: "snit_placed" (Snitplanet findes og ligger i træet). Afbrydelse (ESC, andet værktøj, højreklik): "snit_creation_cancelled"
	// - der efterlades intet halvt Snitplan.

	// Opretter et Snitplan og lader det følge musen som Clip-volumen (Potrees egen insertion: samme mus-konventioner - venstreklik
	// sætter det, ESC/cancel_insertions afbryder). Størrelse efter kameraafstand; lodret (vendt mod kameraet) eller vandret
	// (længderetning = kameraets højre). ESC efterlader intet.
	startPlacement(definition = {}){
		const viewer = this.viewer;
		const volume = viewer.volumeTool.startInsertion({type: SnitVolume, name: definition.name || this._nextName()});
		this._startPlacementOn(volume, {fitToView: true, definition});
		this._track(volume);
		volume.setDefinition(Object.assign({}, definition, {name: definition.name || volume.name}));
		this._changed();
		this.dispatchEvent({type: "snit_added", snit: volume});
		this.setDisplayMode(volume.snit.id, SnitDisplayMode.HIGHLIGHT);
		return volume;
	}

	// Plan udspændt af tre punkter udpeget i punktskyen (samme mus-konventioner som målingerne: venstreklik sætter et punkt, højreklik
	// og ESC afbryder). Punkterne vises som markører undervejs; kollineære punkter afvises ("snit_pick_invalid"), og det tredje
	// punkt kan vælges igen. Fremdrift: "snit_pick_progress" {count}.
	startThreePoints(){
		const viewer = this.viewer;
		viewer.dispatchEvent({type: "cancel_insertions"});

		const dom = viewer.renderer.domElement;
		const input = viewer.inputHandler;
		const target = new EventDispatcher();
		target.name = "snit_three_points";
		const points = [];
		let done = false;

		const markers = new THREE.Points(
			new THREE.BufferGeometry(),
			new THREE.PointsMaterial({color: 0xF28C28, size: 12, sizeAttenuation: false, depthTest: false, depthWrite: false})
		);
		markers.frustumCulled = false;
		markers.renderOrder = 1000;
		markers.name = "snit_three_points_markers";
		viewer.scene.scene.add(markers);
		const updateMarkers = () => {
			// relativt til første punkt: ingen float32-præcisionsproblemer ved UTM (positionen bæres af objektets float64-matrix)
			const origin = points[0] || new THREE.Vector3();
			markers.position.copy(origin);
			const arr = new Float32Array(points.length * 3);
			points.forEach((p, i) => { arr[i * 3] = p.x - origin.x; arr[i * 3 + 1] = p.y - origin.y; arr[i * 3 + 2] = p.z - origin.z; });
			markers.geometry.setAttribute("position", new THREE.BufferAttribute(arr, 3));
			markers.geometry.computeBoundingSphere();
			markers.updateMatrixWorld(true);
			this._changed();
		};

		const finish = () => {
			if(done) return false;
			done = true;
			dom.removeEventListener("mouseup", onMouseUp, false);
			viewer.removeEventListener("cancel_insertions", onCancel);
			viewer.scene.scene.remove(markers);
			markers.geometry.dispose();
			markers.material.dispose();
			// slip pick-målet, så venstre-træk igen navigerer
			if(input.drag && input.drag.object === target) input.startDragging(null);
			this._changed();
			return true;
		};
		const onCancel = () => {
			if(finish()) this.dispatchEvent({type: "snit_creation_cancelled"});
		};
		const onMouseUp = (e) => {
			if(e.button === THREE.MOUSE.RIGHT){
				onCancel();
				return;
			}
			if(e.button !== THREE.MOUSE.LEFT) return;

			const camera = viewer.scene.getActiveCamera();
			const hit = Utils.getMousePointCloudIntersection(input.mouse, camera, viewer, viewer.scene.pointclouds, {pickClipped: false});
			if(hit){
				const candidate = hit.location.clone();
				const next = points.concat([candidate]);
				if(next.length === 3 && !snitplanFromThreePoints(next.map((p) => [p.x, p.y, p.z]))){
					this.dispatchEvent({type: "snit_pick_invalid", count: points.length});
				}else{
					points.push(candidate);
					updateMarkers();
					if(points.length === 3){
						const patch = snitplanFromThreePoints(points.map((p) => [p.x, p.y, p.z]));
						finish();
						const volume = this.add(patch, {activate: true});
						this.dispatchEvent({type: "snit_placed", snit: volume});
						return;
					}
					this.dispatchEvent({type: "snit_pick_progress", count: points.length});
				}
			}
			input.startDragging(target);
		};

		dom.addEventListener("mouseup", onMouseUp, false);
		viewer.addEventListener("cancel_insertions", onCancel);
		input.startDragging(target);
		this.dispatchEvent({type: "snit_pick_progress", count: 0});
	}

	// Færdig definition (fx udledt af en IFC-flade) som almindeligt Snitplan: samme afslutning som de øvrige oprettelsesflows.
	addPlaced(definition = {}){
		const volume = this.add(definition, {activate: true});
		this.dispatchEvent({type: "snit_placed", snit: volume});
		return volume;
	}

	// "Vælg nyt centrum": samme placeringsmekanik på et eksisterende Snitplan (kun centrum ændres). Annullering lader det stå.
	startRepositioning(id){
		const volume = this.get(id);
		if(!volume) return null;
		this.viewer.dispatchEvent({type: "cancel_insertions"});
		this._startPlacementOn(volume, {fitToView: false});
		this.viewer.inputHandler.startDragging(volume);
		return volume;
	}

	_startPlacementOn(volume, {fitToView, definition = {}}){
		const viewer = this.viewer;
		const state = {done: false};
		const original = volume.definition;

		const orient = () => {
			const camera = viewer.scene.getActiveCamera();
			if(definition.orientation === SnitOrientation.HORIZONTAL){
				// vandret plan: længderetningen følger kameraets højre-retning (vandret projektion)
				const right = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0);
				const az = (Math.atan2(right.x, right.y) * 180 / Math.PI + 360) % 360;
				return {azimuth: az, dip: 0, orientation: SnitOrientation.HORIZONTAL};
			}
			const dir = camera.getWorldDirection(new THREE.Vector3());
			// lodret plan vendt mod kameraet: normalen er modsat kamerastrålens vandrette del
			const o = orientationFromNormal([-dir.x, -dir.y, 0], 0);
			return {azimuth: o.azimuth, dip: 90, orientation: SnitOrientation.VERTICAL};
		};

		const drag = (e) => {
			const camera = viewer.scene.getActiveCamera();
			let patch;
			if(fitToView){
				// oprettelse: VolumeTool's egen drag-lytter har allerede sat volume.position fra punktskyen
				patch = {center: [volume.position.x, volume.position.y, volume.position.z]};
			}else{
				// nyt centrum: samme pick som VolumeTool (punktskyen, uden klippede punkter)
				const hit = Utils.getMousePointCloudIntersection(e.drag.end, camera, viewer, viewer.scene.pointclouds, {pickClipped: false});
				if(!hit) return;
				patch = {center: [hit.location.x, hit.location.y, hit.location.z]};
			}
			if(fitToView){
				// som VolumeTool: størrelse efter afstand til kameraet
				const wp = volume.getWorldPosition(new THREE.Vector3()).applyMatrix4(camera.matrixWorldInverse);
				const w = Math.max(Math.round(Math.abs(wp.z / 5) * 2 * 2) / 2, 1);
				Object.assign(patch, orient(), {length: w, height: w}, definition.cube ? {thickness: w} : {}, definition.length ? {length: definition.length} : {}, definition.height ? {height: definition.height} : {});
			}
			volume.setDefinition(patch);
		};

		const finish = () => {
			if(state.done) return;
			state.done = true;
			volume.removeEventListener("drag", drag);
			volume.removeEventListener("drop", onDrop);
			viewer.removeEventListener("cancel_insertions", onCancel);
		};
		const onDrop = () => {
			finish();
			this.dispatchEvent({type: "snit_placed", snit: volume});
		};
		const onCancel = () => {
			if(state.done) return;
			finish();
			if(fitToView){
				// ESC under oprettelse: intet objekt efterlades
				this.remove(volume);
				this.dispatchEvent({type: "snit_creation_cancelled"});
			}else{
				volume.setDefinition({center: original.center});
			}
		};

		volume.addEventListener("drag", drag);
		volume.addEventListener("drop", onDrop);
		viewer.addEventListener("cancel_insertions", onCancel);
		return state;
	}

	// ---- visningstilstand + global clip-tilstand ----------------------------------------------------------------------

	setDisplayMode(id, mode){
		const volume = this.get(id);
		if(!volume) return;
		const next = Object.values(SnitDisplayMode).includes(mode) ? mode : SnitDisplayMode.NORMAL;
		if(next !== SnitDisplayMode.NORMAL){
			// højst ét aktivt Snitplan: det tidligere går tilbage til Normal
			for(const other of this.list()){
				if(other !== volume && other.displayMode !== SnitDisplayMode.NORMAL){
					other.displayMode = SnitDisplayMode.NORMAL;
					this.dispatchEvent({type: "snit_mode_changed", snit: other});
				}
			}
		}
		volume.displayMode = next;
		this._apply();
		this.dispatchEvent({type: "snit_mode_changed", snit: volume});
	}

	// Beregner clip-puljen og den globale clipTask/clipMethod ud fra Snitplanerne.
	_apply(){
		const viewer = this.viewer;
		const snits = this.list();
		const active = snits.find((v) => v.displayMode !== SnitDisplayMode.NORMAL && v.visible) || null;

		for(const v of snits) v.clip = (v === active);
		this.activeVolume = active;

		if(active){
			if(!this._saved) this._saved = {task: viewer.clipTask, method: viewer.clipMethod};
			const mode = active.displayMode;
			const task = mode === SnitDisplayMode.ISOLATE ? ClipTask.SHOW_INSIDE : (mode === SnitDisplayMode.OUTSIDE ? ClipTask.SHOW_OUTSIDE : ClipTask.HIGHLIGHT);
			this._applyClipState(task, ClipMethod.INSIDE_ANY);
		}else if(this._saved){
			const saved = this._saved;
			const applied = this._applied;
			this._saved = null;
			this._applied = null;
			// kun hvis ingen andre har ændret den globale tilstand siden vi satte den
			if(applied && viewer.clipTask === applied.task && viewer.clipMethod === applied.method){
				this._applyClipState(saved.task, saved.method);
				this._applied = null;
			}
		}
		this._changed();
	}

	_applyClipState(task, method){
		this._applying = true;
		try{
			this.viewer.setClipTask(task);
			this.viewer.setClipMethod(method);
		}finally{
			this._applying = false;
		}
		this._applied = {task, method};
	}

	// En ændring af clipTask/clipMethod, vi ikke selv har lavet (Clip-dialogen o.l.): brugeren har overtaget den globale tilstand -
	// Snitplanerne går til Normal uden at røre den.
	_externalClipChange(){
		if(this._applying || !this._applied) return;
		const v = this.viewer;
		if(v.clipTask === this._applied.task && v.clipMethod === this._applied.method) return;
		this._saved = null;
		this._applied = null;
		for(const snit of this.list()){
			if(snit.displayMode !== SnitDisplayMode.NORMAL){
				snit.displayMode = SnitDisplayMode.NORMAL;
				snit.clip = false;
				this.dispatchEvent({type: "snit_mode_changed", snit});
			}
		}
		this._changed();
	}

	_changed(){
		this.version++;
		const viewer = this.viewer;
		if(viewer._requestRedraw) viewer._requestRedraw(); else viewer.needsRedraw = true;
	}

	// ---- IFC-adapter ----------------------------------------------------------------------------------------------------

	// Planerne for det aktive Snitplan i Fremhæv (ellers tom liste): IFC/OBJ-værten tegner et additivt overlay inden for dem, mens
	// resten af modellen fortsat vises (samme faglige betydning som punktskyens HIGHLIGHT). Samme geometri og caching som nedenfor.
	getIfcHighlightPlanes(){
		if(this._ifcHighlightCache.version === this.version) return this._ifcHighlightCache.planes;
		const active = this.list().find((v) => v.displayMode === SnitDisplayMode.HIGHLIGHT && v.visible);
		this._ifcHighlightCache = {version: this.version, planes: active ? snitplanFacePlanes(active.snit) : []};
		return this._ifcHighlightCache.planes;
	}

	// Seks indadvendte planer ({normal: [x, y, z], constant}) for det aktive Snitplan i "Skjul alt andet" (ellers tom liste) -
	// afledt af den fælles definition i float64. Listen genbruges, indtil version ændres (ingen allokering pr. frame).
	getIfcClipPlanes(){
		if(this._ifcCache.version === this.version) return this._ifcCache.planes;
		const active = this.list().find((v) => v.displayMode === SnitDisplayMode.ISOLATE && v.visible);
		this._ifcCache = {version: this.version, planes: active ? snitplanFacePlanes(active.snit) : []};
		return this._ifcCache.planes;
	}

	// Seks UDADvendte planer for det aktive Snit i "Vis udenfor" (ellers tom liste). IFC-værten sætter dem sammen med
	// clipIntersection = true: kun det, der ligger inden for ALLE seks planers klippede side (= inde i boksen), klippes væk.
	getIfcOutsidePlanes(){
		if(this._ifcOutsideCache.version === this.version) return this._ifcOutsideCache.planes;
		const active = this.list().find((v) => v.displayMode === SnitDisplayMode.OUTSIDE && v.visible);
		this._ifcOutsideCache = {version: this.version, planes: active ? snitplanFacePlanes(active.snit, true) : []};
		return this._ifcOutsideCache.planes;
	}

	// ---- konvertering af gamle Clip-bokse ----------------------------------------------------------------------------------

	// En gammel Clip-boks (BoxVolume med clip) bliver til et Snit med samme placering, størrelse og (frie) orientering. Starter i Ingen.
	// Den gamle boks røres ikke her - se convertLegacyClips.
	convertVolume(box, {name} = {}){
		const patch = snitDefinitionFromBox(box);
		const keep = name || (box.name && !/^Clip/i.test(box.name) ? box.name : undefined);
		const snit = this.add(Object.assign(patch, keep ? {name: keep} : {}));
		if(box.visible === false) snit.visible = false;
		return snit;
	}

	// Konverterer ALLE gamle Clip-bokse i scenen (ikke Snit, ikke interne clip-volumener, ikke polygon-klip) og fjerner de gamle.
	convertLegacyClips(){
		const scene = this.viewer.scene;
		if(!scene) return [];
		const old = scene.volumes.filter((v) => v instanceof BoxVolume && v.clip && !v.pvSnit && !v.pvInternal);
		return old.map((box) => {
			const snit = this.convertVolume(box);
			scene.removeVolume(box);
			return snit;
		});
	}
}

export function createSnitplanManager(viewer){
	return new SnitplanManager(viewer);
}

